import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Loader2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Modal } from '@/components/modal';
import { useWorkflowEligibility } from '@/hooks/queries/use-workflow-eligibility';
import { useApplyToWave } from '@/hooks/mutations/use-apply-to-wave';
import { ApplyResult, GateEvaluation } from '@/services/workflow/qualification';
import { cn } from '@/lib/utils';

const CHECK_LABELS: Record<string, string> = {
  end_to_end_run: 'Has completed at least one successful run',
  has_pinnable_version: 'Has a saved version that can be pinned',
  has_description: 'Has a description',
};

// An unrecognised key falls back to the key: a wave can require a check
// this build has never heard of, and hiding it would show a passing
// checklist beside a refusal.
const checkLabel = (key: string) => CHECK_LABELS[key] ?? key;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const CheckRow: FC<{ check: GateEvaluation }> = ({ check }) => (
  <li className='flex items-start gap-2 text-sm'>
    {check.passed ? (
      <Check className='size-4 shrink-0 mt-0.5 text-emerald-600' />
    ) : (
      <X
        className={cn(
          'size-4 shrink-0 mt-0.5',
          check.required ? 'text-red-600' : 'text-amber-500',
        )}
      />
    )}
    <span
      className={cn(
        !check.passed && check.required && 'text-red-700 dark:text-red-400',
      )}
    >
      {checkLabel(check.key)}
      {!check.required && (
        <span className='ml-1.5 text-xs text-muted-foreground'>
          (advisory, does not block)
        </span>
      )}
    </span>
  </li>
);

const Notice: FC<{ tone?: 'muted' | 'warning'; children: ReactNode }> = ({
  tone = 'muted',
  children,
}) => (
  <div className='flex items-start gap-2 rounded-md bg-muted p-3 text-sm'>
    <AlertCircle
      className={cn(
        'size-4 shrink-0 mt-0.5',
        tone === 'warning' ? 'text-amber-500' : 'text-muted-foreground',
      )}
    />
    <span>{children}</span>
  </div>
);

interface ApplyToUatModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string | undefined;
  workflowId: string;
  workflowName: string;
  onApplied?: (result: ApplyResult) => void;
}

export const ApplyToUatModal: FC<ApplyToUatModalProps> = ({
  isOpen,
  onClose,
  orgId,
  workflowId,
  workflowName,
  onApplied,
}) => {
  const [selectedWaveId, setSelectedWaveId] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);

  const {
    data: eligibility,
    isLoading,
    error,
  } = useWorkflowEligibility(orgId, isOpen ? workflowId : undefined);

  const applyMutation = useApplyToWave({
    orgId,
    workflowId,
    workflowName,
    onAdmitted: onApplied,
  });

  // Which workflow this modal is currently open for, readable from inside
  // a mutation callback that outlives the render it was created in.
  const openForRef = useRef<string | null>(null);
  useEffect(() => {
    openForRef.current = isOpen ? workflowId : null;
  }, [isOpen, workflowId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedWaveId(null);
      setResult(null);
      applyMutation.reset();
    }
    // Resetting on close only; adding the mutation would re-run on every
    // status change and clear the verdict the modal is showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Derived rather than set in an effect, so the Select is controlled from
  // the first render. `wave_id` is still sent explicitly on apply.
  const activeWaveId =
    selectedWaveId ?? eligibility?.open_waves[0]?.wave.id ?? null;

  const selected = useMemo(
    () =>
      eligibility?.open_waves.find((w) => w.wave.id === activeWaveId) ?? null,
    [eligibility, activeWaveId],
  );

  const failingRequired = useMemo(
    () => (selected?.checks ?? []).filter((c) => c.required && !c.passed),
    [selected],
  );

  const alreadyInWave = !!eligibility?.blocked_by_wave;
  const nothingOpen = (eligibility?.open_waves.length ?? 0) === 0;
  const isSubmitting = applyMutation.isPending;

  const canConfirm =
    !!eligibility?.any_selectable &&
    !!selected?.can_apply &&
    failingRequired.length === 0 &&
    !isSubmitting;

  const notReadyText = failingRequired.length
    ? `Not ready: ${failingRequired.map((c) => checkLabel(c.key)).join('; ')}.`
    : null;

  const noWaveText = eligibility?.next_wave
    ? `No wave is open. ${eligibility.next_wave.name} opens ${formatDate(eligibility.next_wave.pre_uat_opens_at)}.`
    : 'No wave is currently open for this region.';

  const disabledReason = useMemo(() => {
    if (isSubmitting) return null;
    if (alreadyInWave) {
      return `Already applied to ${eligibility?.blocked_by_wave?.name}. A workflow can be in one wave at a time.`;
    }
    if (nothingOpen) return noWaveText;
    if (notReadyText) return notReadyText;
    if (!eligibility?.any_selectable) {
      return 'This workflow cannot be applied to a wave right now.';
    }
    return null;
  }, [
    isSubmitting,
    alreadyInWave,
    nothingOpen,
    notReadyText,
    noWaveText,
    eligibility,
  ]);

  const handleApply = () => {
    if (!activeWaveId) return;
    applyMutation.mutate(
      { waveId: activeWaveId },
      {
        onSuccess: (applyResult) => {
          // Only if this modal is still showing the workflow that was
          // applied for. Without the guard, an apply still in flight when
          // the modal closes sets the verdict afterwards, and reopening --
          // possibly on a different workflow, since the instance stays
          // mounted -- renders the previous outcome under the new name.
          if (openForRef.current === workflowId) setResult(applyResult);
        },
      },
    );
  };

  let content: ReactNode = null;

  if (isLoading) {
    content = (
      <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Checking eligibility...
      </div>
    );
  } else if (error) {
    content = (
      <div className='flex items-start gap-2 py-4 text-sm text-red-700 dark:text-red-400'>
        <AlertCircle className='size-4 shrink-0 mt-0.5' />
        <span>{error.message}</span>
      </div>
    );
  } else if (result?.admitted) {
    content = (
      <div className='space-y-3 py-2'>
        <div className='flex items-start gap-2 text-sm'>
          <Check className='size-4 shrink-0 mt-0.5 text-emerald-600' />
          <span>
            {result.outcome === 'already_applied'
              ? 'This workflow was already in the wave.'
              : `Admitted to ${result.wave?.name}.`}
            {result.placed
              ? ' A copy now sits in the pre-UAT organisation for review.'
              : ' The copy into pre-UAT did not complete; applying again will retry it.'}
          </span>
        </div>
        {result.attempt?.pinned_version_id && (
          <p className='text-xs text-muted-foreground'>
            Pinned version {result.attempt.pinned_version_id}. Stage:{' '}
            {result.attempt.current_stage}.
          </p>
        )}
      </div>
    );
  } else if (result) {
    content = (
      <div className='space-y-3 py-2'>
        <p className='text-sm'>
          {result.outcome === 'no_open_wave'
            ? 'No wave was open when this was submitted.'
            : 'The entry gate did not admit this workflow.'}
        </p>
        <ul className='space-y-1.5'>
          {result.evaluations.map((c) => (
            <CheckRow key={c.key} check={c} />
          ))}
        </ul>
        {result.next_wave && (
          <p className='text-xs text-muted-foreground'>
            Next wave {result.next_wave.name} opens{' '}
            {formatDate(result.next_wave.pre_uat_opens_at)}.
          </p>
        )}
      </div>
    );
  } else if (eligibility) {
    content = (
      <div className='space-y-4 py-2'>
        {alreadyInWave && (
          <Notice tone='warning'>
            Already applied to{' '}
            <strong>{eligibility.blocked_by_wave?.name}</strong>. A workflow can
            be in one wave at a time.
          </Notice>
        )}

        {nothingOpen && <Notice>{noWaveText}</Notice>}

        {!nothingOpen && eligibility.open_waves.length > 1 && (
          <div className='space-y-1.5'>
            <label className='text-sm font-medium'>Wave</label>
            <Select
              value={activeWaveId ?? undefined}
              onValueChange={setSelectedWaveId}
            >
              <SelectTrigger>
                <SelectValue placeholder='Choose a wave' />
              </SelectTrigger>
              <SelectContent>
                {eligibility.open_waves.map(({ wave }) => (
                  <SelectItem key={wave.id} value={wave.id}>
                    {wave.name} (pre-UAT closes{' '}
                    {formatDate(wave.pre_uat_closes_at)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {!nothingOpen && selected && (
          <div className='space-y-2'>
            <p className='text-sm font-medium'>
              {eligibility.open_waves.length > 1
                ? 'Conditions for this wave'
                : `Conditions for ${selected.wave.name}`}
            </p>
            {selected.checks.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                This wave sets no entry conditions.
              </p>
            ) : (
              <ul className='space-y-1.5'>
                {selected.checks.map((c) => (
                  <CheckRow key={c.key} check={c} />
                ))}
              </ul>
            )}
            {notReadyText && (
              // Also in the confirm button's tooltip, repeated in plain
              // sight because a hover-only explanation is unreachable on
              // touch.
              <p className='text-sm text-red-700 dark:text-red-400 pt-1'>
                {notReadyText}
              </p>
            )}
            <p className='text-xs text-muted-foreground pt-1'>
              On admission the current saved version is pinned and copied into
              the pre-UAT organisation for review.
              {selected.wave.requires_client_sign_off
                ? ' Both Capitol and client sign-off are needed to reach UAT.'
                : ' Capitol sign-off is needed to reach UAT.'}
            </p>
          </div>
        )}
      </div>
    );
  }

  const confirmButton = (
    <Button onClick={handleApply} disabled={!canConfirm} className='gap-2'>
      {isSubmitting && <Loader2 className='size-4 animate-spin' />}
      Apply to UAT
    </Button>
  );

  let footer: ReactNode;
  if (result) {
    footer = <Button onClick={onClose}>Close</Button>;
  } else {
    footer = (
      <>
        <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        {disabledReason ? (
          <Tooltip>
            {/* A disabled button swallows pointer events, so the trigger
                wraps it in a span. */}
            <TooltipTrigger asChild>
              <span className='inline-flex'>{confirmButton}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className='max-w-xs'>{disabledReason}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          confirmButton
        )}
      </>
    );
  }

  return (
    <Modal
      title='Apply to UAT'
      description={workflowName}
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        // Not dismissable mid-apply: the request is a write, and closing
        // over it hides whether it landed.
        if (!open && !isSubmitting) onClose();
      }}
      footer={footer}
      contentClassName='sm:max-w-lg'
    >
      {content}
    </Modal>
  );
};
